import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { Check, ShoppingCart, Package, User, Truck, MapPin, FileText, DollarSign, CreditCard, AlertTriangle, MessageCircle, X } from 'lucide-react';
import { toast } from '../components/Toast';

const PublicShopCheckout = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [shop, setShop] = useState(location.state?.shop || null);
  const [cart, setCart] = useState(location.state?.cart || []);
  const [loading, setLoading] = useState(!location.state);
  const [submitting, setSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    delivery_type: 'pickup',
    notes: ''
  });

  // Auto-sélectionner le bon mode de livraison selon les options activées
  useEffect(() => {
    if (shop) {
      const pickupOk = parseInt(shop.pickup_enabled) === 1;
      const deliveryOk = parseInt(shop.delivery_enabled) === 1;
      if (!pickupOk && deliveryOk) {
        setFormData(prev => ({ ...prev, delivery_type: 'delivery' }));
      } else if (pickupOk && !deliveryOk) {
        setFormData(prev => ({ ...prev, delivery_type: 'pickup' }));
      }
    }
  }, [shop]);

  useEffect(() => {
    if (!location.state) {
      // Si pas de state, charger la boutique et le panier
      loadShopAndCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadShopAndCart = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/boutique/${slug}`);
      const data = await res.json();
      
      if (data.success) {
        setShop(data.shop);
        // Charger le panier du localStorage
        const savedCart = localStorage.getItem(`cart_${slug}`);
        if (savedCart) {
          setCart(JSON.parse(savedCart));
        }
      } else {
        navigate(`/boutique/${slug}`);
      }
    } catch (err) {
      console.error(err);
      navigate(`/boutique/${slug}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(productId);
      return;
    }
    
    setCart(prev => prev.map(item => {
      if (item.product_id === productId) {
        const quantity = Math.min(newQuantity, item.max_stock);
        return { ...item, quantity };
      }
      return item;
    }));
  };

  const removeItem = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const getSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.product_price * item.quantity), 0);
  };

  const getDeliveryFee = () => {
    if (formData.delivery_type === 'delivery' && shop?.delivery_fee) {
      return parseFloat(shop.delivery_fee);
    }
    return 0;
  };

  const getTotal = () => {
    return getSubtotal() + getDeliveryFee();
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${BACKEND_URL}/${path}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.warning('Votre panier est vide');
      return;
    }

    if (!formData.customer_name.trim()) {
      toast.warning('Veuillez entrer votre nom');
      return;
    }

    if (!formData.customer_phone.trim()) {
      toast.warning('Veuillez entrer votre numéro de téléphone');
      return;
    }

    if (formData.delivery_type === 'delivery' && !formData.customer_address.trim()) {
      toast.warning('Veuillez entrer votre adresse de livraison');
      return;
    }

    // Vérifier le montant minimum
    if (shop.min_order_amount && getSubtotal() < parseFloat(shop.min_order_amount)) {
      toast.warning(`Le montant minimum de commande est de ${parseInt(shop.min_order_amount).toLocaleString()} ${shop.currency}`);
      return;
    }

    setSubmitting(true);
    
    try {
      const orderData = {
        ...formData,
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        }))
      };

      const res = await fetch(`${BACKEND_URL}/api/boutique/${slug}/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      const data = await res.json();

      if (data.success) {
        // Vider le panier
        localStorage.removeItem(`cart_${slug}`);
        setCart([]);
        setOrderSuccess(data);
      } else {
        toast.error(data.message || 'Erreur lors de la commande');
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la commande');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Page de succès
  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Commande envoyée !</h1>
          <p className="text-gray-600 mb-4">
            Votre commande a été transmise à {shop.nom}. Vous serez contacté(e) pour la confirmation.
          </p>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="text-sm text-gray-500 mb-1">Numéro de commande</div>
            <div className="text-xl font-bold text-indigo-600">{orderSuccess.order_number}</div>
          </div>

          <div className="bg-indigo-50 rounded-lg p-4 mb-6">
            <div className="text-sm text-indigo-700 mb-1">Total à payer</div>
            <div className="text-2xl font-bold text-indigo-600">
              {parseInt(orderSuccess.total_amount).toLocaleString()} {orderSuccess.currency}
            </div>
          </div>

          <div className="space-y-3">
            <Link
              to={`/commande/${orderSuccess.tracking_token}`}
              className="block w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
            >
              Suivre ma commande
            </Link>
            
            <Link
              to={`/boutique/${slug}`}
              className="block w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              Retour à la boutique
            </Link>
          </div>

          {shop.whatsapp && (
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-gray-500 mb-3">Des questions ?</p>
              <a
                href={`https://wa.me/${shop.whatsapp.replace(/[^0-9]/g, '')}?text=Bonjour, je viens de passer la commande ${orderSuccess.order_number}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
              >
                <MessageCircle className="w-5 h-5" /> Contacter sur WhatsApp
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Panier vide</h1>
          <p className="text-gray-500 mb-6">Ajoutez des produits pour passer commande</p>
          <Link
            to={`/boutique/${slug}`}
            className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            Voir les produits
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to={`/boutique/${slug}`} className="text-gray-500 hover:text-gray-700">
            ← Retour
          </Link>
          <h1 className="text-lg font-bold text-gray-800">Finaliser la commande</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit}>
          {/* Panier */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><ShoppingCart className="w-5 h-5" /> Votre panier</h2>
            <div className="space-y-3">
              {cart.map(item => (
                <div key={item.product_id} className="flex items-center gap-3 py-3 border-b last:border-0">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                    {item.product_image ? (
                      <img src={getImageUrl(item.product_image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">{item.product_name}</div>
                    <div className="text-sm text-indigo-600 font-medium">
                      {parseInt(item.product_price).toLocaleString()} {shop?.currency}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                      className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
                    >
                      -
                    </button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      disabled={item.quantity >= item.max_stock}
                      className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 disabled:opacity-50"
                    >
                      +
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.product_id)}
                    className="text-red-500 hover:text-red-700 p-2"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Informations client */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><User className="w-5 h-5" /> Vos informations</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet *
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Votre nom"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone *
                </label>
                <input
                  type="tel"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="+221 77 123 45 67"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (optionnel)
                </label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="email@exemple.com"
                />
              </div>
            </div>
          </div>

          {/* Mode de livraison */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Truck className="w-5 h-5" /> Mode de récupération</h2>
            <div className="space-y-3">
              {parseInt(shop?.pickup_enabled) === 1 && (
                <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition ${
                  formData.delivery_type === 'pickup' ? 'border-indigo-500 bg-indigo-50' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="radio"
                    name="delivery_type"
                    value="pickup"
                    checked={formData.delivery_type === 'pickup'}
                    onChange={handleChange}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">Retrait sur place</div>
                    {shop?.address && (
                      <div className="text-sm text-gray-500 flex items-center gap-1"><MapPin className="w-4 h-4" /> {shop.address}</div>
                    )}
                  </div>
                  <div className="font-medium text-green-600">Gratuit</div>
                </label>
              )}

              {parseInt(shop?.delivery_enabled) === 1 && (
                <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition ${
                  formData.delivery_type === 'delivery' ? 'border-indigo-500 bg-indigo-50' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="radio"
                    name="delivery_type"
                    value="delivery"
                    checked={formData.delivery_type === 'delivery'}
                    onChange={handleChange}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">Livraison à domicile</div>
                    <div className="text-sm text-gray-500">Nous vous livrons</div>
                  </div>
                  <div className="font-medium text-gray-800">
                    {parseFloat(shop?.delivery_fee || 0) > 0 
                      ? `${parseInt(shop.delivery_fee).toLocaleString()} ${shop?.currency}`
                      : 'Gratuit'
                    }
                  </div>
                </label>
              )}
            </div>

            {formData.delivery_type === 'delivery' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse de livraison *
                </label>
                <textarea
                  name="customer_address"
                  value={formData.customer_address}
                  onChange={handleChange}
                  required={formData.delivery_type === 'delivery'}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Votre adresse complète..."
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><FileText className="w-5 h-5" /> Notes (optionnel)</h2>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Instructions spéciales, remarques..."
            />
          </div>

          {/* Récapitulatif */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5" /> Récapitulatif</h2>
            <div className="space-y-2">
              <div className="flex justify-between text-gray-600">
                <span>Sous-total ({cart.reduce((sum, i) => sum + i.quantity, 0)} articles)</span>
                <span>{getSubtotal().toLocaleString()} {shop?.currency}</span>
              </div>
              {getDeliveryFee() > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Frais de livraison</span>
                  <span>{getDeliveryFee().toLocaleString()} {shop?.currency}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold text-gray-800 pt-2 border-t">
                <span>Total</span>
                <span>{getTotal().toLocaleString()} {shop?.currency}</span>
              </div>
            </div>
            
            {shop?.min_order_amount > 0 && getSubtotal() < parseFloat(shop.min_order_amount) && (
              <div className="mt-4 p-3 bg-amber-50 text-amber-700 rounded-lg text-sm flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> Montant minimum requis: {parseInt(shop.min_order_amount).toLocaleString()} {shop.currency}
              </div>
            )}
          </div>

          {/* Bouton commander */}
          <button
            type="submit"
            disabled={submitting || (shop?.min_order_amount > 0 && getSubtotal() < parseFloat(shop.min_order_amount))}
            className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold text-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {submitting ? 'Envoi en cours...' : `Commander • ${getTotal().toLocaleString()} ${shop?.currency}`}
          </button>

          <p className="text-center text-sm text-gray-500 mt-4 flex items-center justify-center gap-1">
            <CreditCard className="w-4 h-4" /> Paiement à la livraison / récupération
          </p>
        </form>
      </div>
    </div>
  );
};

export default PublicShopCheckout;
